import React from 'react';

/**
 * ComplyKit ButtonGroup: adjacent actions fused into one bar. A pagination
 * pair, a bulk-action bar, the attach-and-comment pair on an answer. The
 * attached look says "these belong together"; it never says "choose one",
 * which is SegmentedControl's job.
 *
 * The bar owns the chrome and the children are quiet inside it, the same
 * idea as SegmentedControl's track: one edge around the bar, hairline
 * dividers between neighbours, inner corners squared (the `attached`
 * treatment, tokens.md). Children are ghost controls (IconButton's default,
 * `variant="ghost"` on a Button) and keep their own size and tone; Chakra's
 * group broadcast `size` and `variant` onto its children, which made a
 * button's appearance depend on where it sat, so nothing is broadcast here.
 *
 * Any control can sit in it: Button, IconButton, a Menu's trigger, a
 * read-only cell beside the buttons (the pagination count). Tab moves
 * through the children as it would anywhere; the group is a `group` named
 * by `label`, so assistive technology hears what the bar is for.
 */
export interface ButtonGroupProps {
  /** The bar's accessible name: what the actions are for ("Pagination", "Bulk actions"). */
  label: string;
  /** The controls, in reading order: ghost controls, each with its own size and tone. */
  children: React.ReactNode;
  id?: string;
}

export const ButtonGroup = React.forwardRef<HTMLDivElement, ButtonGroupProps>(function ButtonGroup(
  { label, children, ...props },
  ref,
) {
  return (
    <div
      {...props}
      ref={ref}
      role="group"
      aria-label={label}
      className="ck:attached"
      data-component="button-group"
    >
      {children}
    </div>
  );
});
